<template>
  <LayoutPage :title="pageTitle" :loading="loading">
    <template v-slot:title>
      <span
        v-if="titleOrgName"
        class="headline font-weight-bold grey--text text--darken-2 mr-1"
        >{{ titleOrgName }}</span
      >
      <span style="color: rgb(255, 65, 54); font-weight: bold" class="headline"
        >+Visual</span
      >
      <span
        style="font-weight: bold"
        class="headline"
        :style="$vuetify.theme.dark ? 'color: white' : 'color: rgb(4, 43, 93)'"
        >Verify Enterprise</span
      >
    </template>
    <div>
      <div class="sticky-container d-flex align-center">
        <v-col md="8">
          <RegionAndFacilitySelect />
        </v-col>
        <v-col md="5">
          <v-row align="center">
            <DateTimeFilter
              v-model="dateRange"
              :ranges="['Today', '2d', '1w', '1m']"
              default="1m"
              custom
              class="black-border"
              time-zone="America/New_York"
            />
            <div class="ml-2">
              <IncidentFilter
                :showTypeFilter="true"
                :showStatusFilter="false"
                :showDispositionFilter="true"
                :showAccessCodeFilter="false"
                :showLocationFilter="false"
                :showSeverityFilter="true"
                :showOverdueFilter="true"
                @apply="applyFilter"
              />
            </div>
          </v-row>
        </v-col>
      </div>

      <!-- Video Grid Section -->
      <v-card class="mt-4">
        <v-card-title class="d-flex align-center">
          <v-row class="d-flex align-center">
            <v-col>
              <v-tabs v-model="activeTab" background-color="transparent">
                <v-tab>
                  <span>
                    <v-icon left color="rgb(255, 65, 54)"
                      >mdi-play-circle</v-icon
                    >
                    Incidents
                  </span>
                </v-tab>
                <v-tab>
                  <span>
                    <v-icon left color="#0084FF">mdi-play-circle</v-icon>
                    Monitor Smart Facilities
                  </span>
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <VideoGridIncidents
                ref="videoGridIncidents"
                :items-per-page="8"
                display-mode="compact"
                :show-location="true"
                :parent-id="defaultOrganization.id"
                :selected-facilities="selectedFacilities"
                :use-facility-filtering="true"
                :external-date-range="dateRange"
                :hide-date-filter="true"
                :incident-filter="filter"
                @open-video="handleOpenVideo"
              />

              <!-- Incident History Section -->
              <v-row class="mt-4" v-show="activeTab === 0">
                <v-col>
                  <v-card outlined flat tile class="black-border">
                    <v-card-title class="title" style="font-weight: 500">
                      Incident & Task Management
                    </v-card-title>
                    <v-skeleton-loader
                      :loading="loading"
                      type="table-thead, table-row-divider@7"
                      class="mx-3"
                    >
                      <v-card-text>
                        <!-- Add EventVideoDialog component -->
                        <EventVideoDialog
                          v-model="showEventDialog"
                          :thumbnail="currentThumbnail"
                          @closed="handleEventDialogClosed"
                        />

                        <DialogViewTask
                          v-if="isOpenViewTask"
                          v-model="isOpenViewTask"
                          :task-id="selectedTaskId"
                        />

                        <v-data-table
                          :headers="headers"
                          :items="incidents"
                          :custom-filter="customFilter"
                          :custom-sort="customSort"
                          :search="search"
                          :sort-by.sync="tableState.sortBy"
                          :sort-desc.sync="tableState.sortDesc"
                          :page.sync="tableState.page"
                          :items-per-page.sync="tableState.itemsPerPage"
                          :footer-props="{
                            'items-per-page-options': [10, 25, 50, 100],
                          }"
                          class="custom-table elevation-1 custom-pagination"
                        >
                          <template #item="{ item }">
                            <tr>
                              <td>
                                <ButtonView
                                  title="View"
                                  @click="gotoZone(item)"
                                />
                              </td>
                              <td>{{ item.incidentType }}</td>
                              <td style="min-width: 200px">
                                <div>
                                  <strong>Facility</strong> :
                                  {{ item.incidentLocation }}
                                </div>
                                <div class="mt-2">
                                  <strong>Smart Unit / Zone</strong> :
                                  {{ item.zoneName }}
                                </div>
                              </td>

                              <td>
                                <SeverityChip
                                  :severity="titleCase(item.priority)"
                                />
                              </td>

                              <!-- Add the video view column implementation -->
                              <td>
                                <div>
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <span v-bind="attrs" v-on="on">
                                        <v-btn
                                          depressed
                                          :disabled="
                                            !isVideoAvailable(item.startedAt)
                                          "
                                          :color="
                                            isVideoAvailable(item.startedAt)
                                              ? 'transparent'
                                              : 'grey'
                                          "
                                          title="View Video"
                                          @click="openVideoDialog(item)"
                                        >
                                          <v-icon
                                            :color="
                                              isVideoAvailable(item.startedAt)
                                                ? 'error'
                                                : 'grey'
                                            "
                                            left
                                          >
                                            fas fa-play-circle
                                          </v-icon>
                                          View Video
                                        </v-btn>
                                      </span>
                                    </template>
                                    <span
                                      v-if="!isVideoAvailable(item.startedAt)"
                                    >
                                      Video is not available. Videos are only
                                      retained for
                                      {{ eenVideoRetentionDays }} days.
                                    </span>
                                    <span
                                      v-if="isVideoAvailable(item.startedAt)"
                                    >
                                      Click here to view the video.
                                    </span>
                                  </v-tooltip>
                                </div>
                              </td>

                              <td>
                                <div>
                                  {{ item.incidentDisposition }}
                                  <div
                                    v-if="item.isOverdue"
                                    class="overdue-text mt-1 justify-center"
                                  >
                                    Overdue!
                                  </div>
                                </div>
                              </td>

                              <td>{{ item.responder }}</td>
                              <td>
                                {{ formatDateTime4Humans(item.startedAt) }}
                              </td>
                              <td>{{ item.dueAt }}</td>
                              <td>{{ item.statusText }}</td>
                            </tr>
                          </template>
                          <template v-slot:no-data>
                            <div class="text-center py-4">
                              <p class="mb-0">
                                No incidents to display.
                                <span v-if="paginationTracking.hasMore.newer">
                                  Return to previous page
                                </span>
                              </p>
                            </div>
                          </template>

                          <template #footer.prepend>
                            <ButtonExportCSV
                              class="order-last"
                              :table-data="incidents"
                              :headers="headers"
                              filename="Incident Disposition Report"
                            />
                          </template>
                        </v-data-table>
                        <div
                          class="d-flex justify-center my-4 custom-pagination-arrow"
                        >
                          <v-btn
                            class="ml-4"
                            icon
                            :disabled="!paginationTracking.hasMore.newer"
                            @click="loadNextIncidents"
                            :loading="isLoading"
                          >
                            <v-icon>mdi-chevron-left</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            :disabled="!paginationTracking.hasMore.older"
                            @click="loadPreviousIncidents"
                            :loading="isLoading"
                          >
                            <v-icon>mdi-chevron-right</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-skeleton-loader>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <EnterpriseVideoGrid
                ref="videoGrid"
                :selected-facilities="selectedFacilities"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </div>
  </LayoutPage>
</template>

<script>
import { computed, ref, watch } from "vue";
import useUser from "@components/authentication/useUser";
import RegionAndFacilitySelect from "../RegionAndFacilitySelect.vue";
import LayoutPage from "@layout/LayoutPage.vue";
import useDashboard from "../../dashboard/useDashboard";
import useAccessibleScope from "@components/authentication/useAccessibleScope";
import useDashboardTitle from "../useDashboardTitle";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonView from "@atoms/ButtonView.vue";
import { INCIDENT_HISTORY } from "../../../components/reports/graphql";
import useDataTable from "../../common/useDataTable";
import {
  incidentStatus,
  formatIncidentType,
} from "../../common/helpers/incident";
import { formatPhoneNumber } from "@tod-ui/helpers/phone";
import { diff } from "@tod-ui/helpers/datetime";
import { stringSortCollator } from "@tod-ui/helpers/strings";
import useFacility from "../../authentication/useFacility";
import { titleCase, toUpperCase } from "@tod-ui/helpers/strings";
import IncidentFilter from "../../reports/IncidentFilter.vue";
import SeverityChip from "@tod-ui/components/SeverityChip.vue";
import VideoGridIncidents from "../../smart_zones/VideoGridIncidents.vue";
import EnterpriseVideoGrid from "./EnterpriseVideoGrid.vue";
import EventVideoDialog from "../../smart_zones/EventVideoDialog.vue";
import useAlerts from "@tod-ui/composables/useAlerts";
import { GET_ALL_ZONE_DEVICES } from "./graphql";
import DialogViewTask from "../../manage_tasks/DialogViewTask.vue";

export default {
  name: "PageVisualVerifyEnterprise",
  components: {
    RegionAndFacilitySelect,
    LayoutPage,
    DateTimeFilter,
    ButtonExportCSV,
    ButtonView,
    SeverityChip,
    IncidentFilter,
    VideoGridIncidents,
    EnterpriseVideoGrid,
    EventVideoDialog,
    DialogViewTask,
  },
  setup() {
    const activeTab = ref(0);
    const { defaultOrganization } = useUser();
    const { addAlert, clearAllAlerts } = useAlerts();

    const pageTitle = useDashboardTitle("Visual Verify Enterprise");
    // The org name prefixes the branded title only for a single region/org
    // scope; a cross-region / multi-org user's scope spans several orgs, so the
    // single default-org name would misrepresent it.
    const { hasCrossOrganizationScope } = useAccessibleScope();
    const titleOrgName = computed(() =>
      hasCrossOrganizationScope.value
        ? ""
        : defaultOrganization.value?.name || ""
    );
    const { organizationsLoading, selectedFacilities, facilityIds } =
      useDashboard();
    const { tableState } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["startedAt"],
      sortDesc: [true],
    });

    const {
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
    } = useFacility();

    const isLoading = ref(true);
    const activeParentId = computed(() => {
      if (selectedFacilities.value && selectedFacilities.value.length > 0) {
        return selectedFacilities.value[0].id;
      }
      return defaultOrganization.value?.id;
    });

    return {
      activeTab,
      isLoading,
      defaultOrganization,
      pageTitle,
      titleOrgName,
      organizationsLoading,
      selectedFacilities,
      facilityIds,
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      tableState,
      activeParentId,
      addAlert,
      clearAllAlerts,
    };
  },

  data() {
    return {
      search: null,
      dateRange: {
        after: new Date(
          new Date().setMonth(new Date().getMonth() - 1)
        ).toISOString(),
        before: new Date().toISOString(),
      },
      filter: {},
      incidentInfo: {},
      isOpenViewTask: false,
      selectedTaskId: null,
      showEventDialog: false,
      currentThumbnail: null,
      eenVideoRetentionDays: 7,
      headers: [
        { text: "Details", value: "details" },
        { text: "Incident Type", value: "incident_type" },
        { text: "Incident Location", value: "task_location" },
        { text: "Incident / Task Priority", value: "severity" },
        { text: "Video Status", value: "viewVideo" },
        { text: "Incident Disposition", value: "incident_disposition" },
        { text: "Renter Details", value: "renter_details" },
        { text: "Started On", value: "started_on" },
        { text: "Target Close Date", value: "target_close_date" },
        { text: "Status", value: "status" },
      ],
      paginationTracking: {
        minSeqId: null,
        maxSeqId: null,
        seqIdLt: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
      },
      organizationIds: [],
      zoneDevicesLoading: false,
    };
  },

  computed: {
    loading() {
      const { incidents } = this.$apollo.queries || {};
      return (
        incidents?.loading ||
        this.isLoading ||
        this.zoneDevicesLoading ||
        (!incidents && this.selectedFacilities.length > 0)
      );
    },
  },

  apollo: {
    incidents: {
      query: INCIDENT_HISTORY,
      variables() {
        const queryVariables = {
          // Scoped by the selected facilities and the zone organizations found
          // beneath them; a single root would AND with those and exclude every
          // facility outside the default organization.
          organizationSubtypes: ["zone"],
          dispositions:
            this.filter.dispositions?.length > 0
              ? this.filter.dispositions
              : ["open", "in_progress", "in_review"],
          dateTimeFilter: this.dateRange,
          limit:
            this.tableState.itemsPerPage > 0
              ? this.tableState.itemsPerPage
              : null,
          seqIdLt: this.paginationTracking.seqIdLt,
          parentIds: this.facilityIds,
          organizationIds:
            this.organizationIds.length > 0 ? this.organizationIds : undefined,
        };

        if (this.filter.types && this.filter.types.length > 0) {
          queryVariables.types = this.filter.types;
        }

        if (this.filter.duringAccessHours !== undefined) {
          queryVariables.duringAccessHours = this.filter.duringAccessHours;
        }

        if (this.filter.severity && this.filter.severity.length > 0) {
          queryVariables.priorities = this.filter.severity;
        } else {
          queryVariables.priorities = [
            "critical",
            "severe",
            "moderate",
            "substantial",
            "low",
          ];
        }

        if (this.filter.isOverdue) {
          queryVariables.overdue = true;
        }

        const {
          severity,
          isOverdue,
          types,
          duringAccessHours,
          ...otherFilters
        } = this.filter;

        return {
          ...queryVariables,
          ...otherFilters,
        };
      },
      skip() {
        return (
          !this.dateRange?.after ||
          !this.organizationIds ||
          this.organizationIds.length === 0
        );
      },
      update({ incidents }) {
        this.isLoading = false;
        const selectedAssigneeId = this.selectedAssigneeId;
        const selectedDisposition = this.selectedDisposition;
        this.paginationTracking.hasMore.newer =
          this.paginationTracking.historyStack.length > 0;

        let filteredIncidents = incidents.filter((incident) => {
          if (selectedAssigneeId && selectedAssigneeId !== "") {
            const hasMatchingAssignee = incident.tasks.some(
              (task) => task.assignee && task.assignee.id === selectedAssigneeId
            );
            if (!hasMatchingAssignee) {
              return false;
            }
          }
          if (selectedDisposition && selectedDisposition !== "") {
            if (incident.disposition !== selectedDisposition) {
              return false;
            }
          }

          return true;
        });

        const processedData = filteredIncidents
          .filter((incident) => {
            const url = new URL(window.location.href);
            const searchParams = url.searchParams;
            const status = searchParams.get("status");

            const hasEENCamera = incident.organization?.devices?.find(
              (d) => d.type === "een" && d.subtype === "video_camera"
            );

            if (!hasEENCamera) return false;

            return true;
          })
          .map((incident) => {
            const responder = incident.responders?.length
              ? incident.responders.sort(
                  (a, b) => a.escalationLevel - b.escalationLevel
                )[0]
              : "--";

            const endedAt = incident.closedAt;
            const isOverdue = incident.tasks?.some(
              (task) =>
                new Date(task.dueAt) < new Date() &&
                task.resolution !== "completed"
            );

            return {
              ...incident,
              shortId: incident.shortId,
              shortCode: incident.shortCode,
              incidentLocation: incident.alerting_strategy.facility_org.name,
              zoneName: incident.organization.name,
              incidentType: formatIncidentType(incident),
              incidentDisposition: incident.disposition,
              priority: incident.tasks[0]?.priority,
              responder: this.formatResponder(responder),
              startedAt: incident.startedAt,
              endedAt,
              duration: diff(incident.startedAt, endedAt).as("minutes"),
              statusText: incidentStatus(incident),
              dueAt:
                incident.tasks?.length > 0
                  ? this.facilityFormatDateTime4Humans(incident.tasks[0]?.dueAt)
                  : "NA",
              isOverdue,
            };
          });

        if (processedData.length > 0) {
          const sortedBySeqId = [...processedData].sort(
            (a, b) => a.seqId - b.seqId
          );
          this.paginationTracking.minSeqId = sortedBySeqId[0].seqId;
          this.paginationTracking.maxSeqId =
            sortedBySeqId[sortedBySeqId.length - 1].seqId;
          this.paginationTracking.hasMore.older =
            processedData.length >= this.tableState.itemsPerPage;
        } else {
          this.paginationTracking.hasMore.older = false;
        }

        return processedData;
      },
      error() {
        this.isLoading = false;
        this.paginationTracking.hasMore.newer =
          this.paginationTracking.historyStack.length > 0;
      },
    },
  },

  watch: {
    selectedFacilities: {
      handler(newVal) {
        this.paginationTracking = {
          minSeqId: null,
          maxSeqId: null,
          seqIdLt: null,
          hasMore: {
            newer: false,
            older: false,
          },
          historyStack: [],
        };
        this.incidents = [];
        if (this.$refs.videoGridIncidents) {
          this.$refs.videoGridIncidents.resetPagination();
        }

        if (!newVal || newVal.length === 0) {
          this.organizationIds = [];
          this.isLoading = false;
          return;
        }

        this.fetchZoneDevices();
      },
      deep: true,
    },
    "tableState.itemsPerPage"() {
      this.resetPagination();
    },
    dateRange: {
      deep: true,
      handler() {
        this.resetPagination();
      },
    },
    filter: {
      deep: true,
      handler() {
        this.resetPagination();
      },
    },
  },

  mounted() {
    if (this.facilityIds.length > 0) {
      this.fetchZoneDevices();
    }
  },

  methods: {
    // `devices` accepts only a single `parentId`, so the selected facilities are
    // fetched one at a time and merged. The devices themselves are not rendered
    // — this exists to discover which zone organizations have cameras beneath
    // the selected facilities, which is what scopes the incident query.
    async fetchZoneDevices() {
      const facilityIds = this.facilityIds;
      if (!facilityIds.length) {
        this.organizationIds = [];
        this.isLoading = false;
        return;
      }

      this.zoneDevicesLoading = true;
      try {
        const results = await Promise.all(
          facilityIds.map((parentId) =>
            this.$apollo.query({
              query: GET_ALL_ZONE_DEVICES,
              variables: { parentId, type: "een" },
              fetchPolicy: "network-only",
            })
          )
        );

        const devices = results.flatMap(({ data }) => data?.devices || []);
        const selectedFacilityIds = new Set(facilityIds);
        const organizationIds = devices
          .filter((device) => {
            const facilityAncestor = device.organization.ancestors.find(
              (ancestor) => selectedFacilityIds.has(ancestor.id)
            );
            return !!facilityAncestor && device.organization.subtype === "zone";
          })
          .map((device) => device.organization.id);

        this.organizationIds = [...new Set(organizationIds)];

        if (this.organizationIds.length > 0) {
          this.loadIncidentHistory();
        } else {
          this.isLoading = false;
        }
      } catch (error) {
        this.organizationIds = [];
        this.isLoading = false;
      } finally {
        this.zoneDevicesLoading = false;
      }
    },

    handleOpenVideo(thumbnail) {
      if (this.$refs.videoGrid) {
        this.$refs.videoGrid.openVideoDialog({
          externalId: thumbnail.actorId,
        });
      }
    },

    gotoZone(incident) {
      this.incidentInfo.value = incident;
      const firstTask = incident.tasks?.[0];
      if (firstTask?.id) {
        this.selectedTaskId = firstTask.id;
        this.isOpenViewTask = true;
      } else {
        this.$router.push({
          name: "PageIncidentInfo",
          params: {
            organization_id: incident.organization.ancestors[0].id,
            incident_id: incident.id,
          },
        });
      }
    },

    formatDateTime4Humans(datetime) {
      return this.facilityFormatDateTime4Humans(datetime);
    },

    applyFilter(filter) {
      const _filter = { ...filter };

      _filter.organizationSubtypes = _filter.locations;
      delete _filter.locations;

      if (_filter.severityStatus?.length) {
        _filter.severity = _filter.severityStatus;
      }
      delete _filter.severityStatus;

      if (_filter.overdueStatus?.length) {
        _filter.isOverdue = _filter.overdueStatus.includes("overdue");
      }
      delete _filter.overdueStatus;

      if (_filter.statuses?.length) {
        _filter.resolutions = [];

        if (_filter.statuses.includes("authorized")) {
          _filter.resolutions.push("claimed");
        }

        if (_filter.statuses.includes("unauthorized")) {
          _filter.resolutions.push("denied");
          _filter.resolutions.push("surveyed");
        }

        if (_filter.statuses.includes("unacknowledged")) {
          _filter.resolutions.push("unexplained");
        }
      }
      delete _filter.statuses;

      // Clean up null/empty values
      for (let k in _filter) {
        if (_filter[k] === null) {
          delete _filter[k];
        } else if (typeof _filter[k] === "boolean" && !_filter[k]) {
          delete _filter[k];
        } else if (Array.isArray(_filter[k]) && !_filter[k].length) {
          delete _filter[k];
        }
      }
      if (_filter.afterHours) {
        _filter["duringAccessHours"] =
          _filter.afterHours === "after-hours" ? false : undefined;
      }
      delete _filter.afterHours;

      this.filter = _filter;
      this.loadIncidentHistory();
      this.$nextTick(() => {
        const videoGridComponent = this.$refs.videoGrid;
        if (
          videoGridComponent &&
          typeof videoGridComponent.fetchIncidents === "function"
        ) {
          videoGridComponent.fetchIncidents();
        }
      });
    },

    customFilter(value, search, item) {
      if (!search) return true;
      const _search = search.toLowerCase();
      const match = [
        item.zoneName.toLowerCase(),
        item.incidentLocation.toLowerCase(),
        item.incidentType.toLowerCase(),
        item.incidentDisposition.toLowerCase(),
        item.shortId.toLowerCase(),
        item.priority.toLowerCase(),
        item.responder.toLowerCase(),
        this.facilityFormatDateTime4Humans(item.startedAt),
      ].join(" ");

      return match.indexOf(_search) >= 0;
    },

    customSort(items, sortBy, sortDesc) {
      const _sortBy = sortBy[0];
      const _sortDesc = sortDesc[0] ? -1 : 1;

      items.sort((a, b) => {
        if (_sortBy === "statusText") {
          const aStatus = `${a.status} ${a.resolution}`;
          const bStatus = `${b.status} ${b.resolution}`;

          return (bStatus < aStatus ? -1 : 1) * _sortDesc;
        } else {
          if (typeof a[_sortBy] === "string") {
            return (
              stringSortCollator.compare(a[_sortBy], b[_sortBy]) * _sortDesc
            );
          }
          return (b[_sortBy] < a[_sortBy] ? -1 : 1) * _sortDesc;
        }
      });

      return items;
    },

    formatResponder(responder) {
      if (!responder?.name) return "--";
      if (!responder?.contactMethod?.value) return responder.name;
      return `${responder.name} @ ${formatPhoneNumber(
        responder.contactMethod.value
      )}`;
    },

    loadIncidentHistory() {
      this.paginationTracking.seqIdLt = null;
      if (!this.organizationIds || this.organizationIds.length === 0) {
        this.incidents = [];
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      this.$nextTick(() => {
        if (this.$apollo.queries.incidents) {
          this.$apollo.queries.incidents
            .refetch()
            .then(() => {})
            .catch((error) => {
              console.error("Error fetching incidents:", error);
              this.isLoading = false;
            });
        } else {
          this.isLoading = false;
        }
      });
    },

    isVideoAvailable(startedAt) {
      if (!this.eenVideoRetentionDays) return false;

      const now = new Date();
      const startDate = new Date(startedAt);
      const diffInDays = (now - startDate) / (1000 * 60 * 60 * 24);

      return diffInDays <= this.eenVideoRetentionDays;
    },

    handleEventDialogClosed() {
      this.showEventDialog = false;
      this.currentThumbnail = null;
    },

    openVideoDialog(item) {
      if (!item) return;
      const cameraId = item.organization?.devices?.find(
        (d) => d.type === "een" && d.subtype === "video_camera"
      )?.externalId;

      if (!cameraId) {
        console.warn("No video camera associated with this incident.");
        this.addAlert({
          type: "error",
          message: "No video camera associated with this incident.",
          timeout: 10,
        });
        return;
      }

      this.currentThumbnail = {
        actorId: cameraId,
        startTimestamp: item.startedAt,
        endedAt: item.closedAt || item.endedAt,
        organizationId:
          item.organization?.ancestors[0]?.id || this.defaultOrganization.id,
        organization: item.organization || {
          ancestors: [{ id: this.defaultOrganization.id }],
        },
        priority: item.priority,
        incidentType: item.incidentType,
        deviceLocation: item.incidentLocation,
        zoneName: item.zoneName || item.organization?.name,
        events: item.events || [],
      };

      this.showEventDialog = true;
    },

    loadNextIncidents() {
      if (this.paginationTracking.historyStack.length === 0) {
        return;
      }
      this.isLoading = true;
      const previousState = this.paginationTracking.historyStack.pop();
      this.paginationTracking.seqIdLt = previousState.seqIdLt;
      this.$nextTick(() => {
        this.$apollo.queries.incidents.refetch().catch((error) => {
          console.error("Error fetching previous page:", error);
          this.isLoading = false;
        });
      });
    },

    loadPreviousIncidents() {
      if (!this.paginationTracking.minSeqId) return;
      this.paginationTracking.historyStack.push({
        seqIdLt: this.paginationTracking.seqIdLt,
        minSeqId: this.paginationTracking.minSeqId,
        maxSeqId: this.paginationTracking.maxSeqId,
      });
      this.isLoading = true;
      this.paginationTracking.seqIdLt = this.paginationTracking.minSeqId;
      this.$nextTick(() => {
        this.$apollo.queries.incidents.refetch().catch((error) => {
          console.error("Error fetching older incidents:", error);
          this.isLoading = false;
        });
      });
    },

    resetPagination() {
      this.paginationTracking = {
        minSeqId: null,
        maxSeqId: null,
        seqIdLt: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
      };
    },

    titleCase,
    toUpperCase,
  },
};
</script>
<style scoped>
.overdue-text {
  color: red;
  font-weight: bold;
}

.custom-pagination .v-data-footer__icons-before,
.custom-pagination .v-data-footer__icons-after {
  visibility: hidden !important;
}
.custom-pagination .v-data-footer__pagination {
  display: none !important;
}
.custom-pagination-arrow {
  position: absolute;
  bottom: 10px;
  right: 60px;
}
</style>
